import React, { useState, useEffect } from "react";
import { Agent } from "../../redux/reducers/agentSlice";
import { Button, View, Text, Card, DropdownField } from "../base";
import { Icon } from "../base/Icon";
import { InputField } from "../base/InputField";
import { TelephonyConfigurationDropdown } from "../profile/TelephonyConfigurationDropdown";
import { useAuth } from "../../hooks/useAuth";

interface AgentSettingsModalProps {
  agent: Agent | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedAgent: Partial<Agent>) => void;
  onMenuAction?: (action: string) => void;
}

export const AgentSettingsModal: React.FC<AgentSettingsModalProps> = ({ agent, isOpen, onClose, onSave, onMenuAction }) => {
  const { isAdmin } = useAuth();
  const [formData, setFormData] = useState<Partial<Agent>>({
    name: "",
    description: "",
    telephonyId: null,
    strategy: "cascade",
  });

  useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name || "",
        description: agent.description || "",
        telephonyId: agent.telephonyId || null,
        strategy: agent.strategy || "cascade",
      });
    }
  }, [agent]);

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const handleCancel = () => {
    // Reset form data to original values
    if (agent) {
      setFormData({
        name: agent.name || "",
        description: agent.description || "",
        telephonyId: agent.telephonyId || null,
        strategy: agent.strategy || "cascade",
      });
    }
    onClose();
  };

  if (!isOpen || !agent) return null;

  return (
    <View className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <View className="p-6">
          {/* Header */}
          <View className="flex items-center justify-between mb-6">
            <Text className="text-xl font-semibold text-gray-900 dark:text-white">Agent Settings</Text>
            <Button
              variant="ghost"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
            >
              <Icon
                name="close"
                size={20}
              />
            </Button>
          </View>

          {/* Form Fields */}
          <View className="space-y-6">
            <View>
              <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Name</Text>
              <InputField
                name="name"
                placeholder="Enter agent name"
                value={formData.name || ""}
                onChange={(value) => setFormData({ ...formData, name: value })}
                className="w-full"
              />
            </View>

            <View>
              <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</Text>
              <InputField
                name="description"
                placeholder="Enter agent description"
                type="textarea"
                value={formData.description || ""}
                onChange={(value) => setFormData({ ...formData, description: value })}
                className="w-full"
              />
            </View>

            <View>
              <TelephonyConfigurationDropdown
                value={formData.telephonyId || ""}
                onChange={(value) => setFormData({ ...formData, telephonyId: value || null })}
                organizationId={agent.organizationId}
              />
            </View>

            {isAdmin && (
              <DropdownField
                className="w-full"
                label="Strategy"
                placeholder="Select Strategy"
                onChange={(value) => setFormData({ ...formData, strategy: value })}
                value={formData.strategy || "cascade"}
                options={[
                  {
                    label: (
                      <View className="flex flex-row items-center gap-2">
                        <Icon
                          name="layers"
                          size={16}
                          className="text-gray-600 dark:text-gray-400"
                        />
                        <Text>Cascade</Text>
                      </View>
                    ),
                    value: "cascade",
                  },
                  {
                    label: (
                      <View className="flex flex-row items-center gap-2">
                        <Icon
                          name="zap"
                          size={16}
                          className="text-gray-600 dark:text-gray-400"
                        />
                        <Text>Speech to Speech</Text>
                      </View>
                    ),
                    value: "sts",
                  },
                ]}
              />
            )}

            <View>
              <View className="space-y-2">
                {isAdmin && (
                  <button
                    onClick={() => {
                      onMenuAction && onMenuAction("recipe");
                      onClose();
                    }}
                    className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                  >
                    <Icon
                      name="recipe"
                      size={14}
                      className="mr-2"
                    />
                    Create Recipe
                  </button>
                )}
                <button
                  onClick={() => onMenuAction && onMenuAction("delete")}
                  className="flex items-center text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                >
                  <Icon
                    name="trash"
                    size={14}
                    className="mr-2"
                  />
                  Delete Agent
                </button>
              </View>
            </View>
          </View>

          {/* Action Buttons */}
          <View className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="secondary"
              onClick={handleCancel}
              className="px-6"
            >
              Cancel
            </Button>
            <Button
              variant="brand"
              onClick={handleSave}
              className="px-6"
            >
              Save
            </Button>
          </View>
        </View>
      </Card>
    </View>
  );
};
